// In-house replacement for pixi-particles 4.x (which has no Pixi v8 support).
// Implements exactly the feature subset used by emitterData.js / EmitterManager:
// alpha/scale/color/speed interpolation, acceleration + maxSpeed, start rotation,
// rotation speed, circle/ring/burst/point spawn, segmented quadratic ease, frame
// animation (confetti), playOnce lifecycle, pooling-friendly cleanup/reparent.
import * as PIXI from 'pixi.js';

// marker kept for API compatibility: `emitter.particleConstructor = AnimatedParticle`
export class AnimatedParticle {}

const DEG2RAD = Math.PI / 180;

const hexToRgb = hex => {
    const parts = hex.replace('#', '').match(/.{1,2}/g);
    return [parseInt(parts[0], 16), parseInt(parts[1], 16), parseInt(parts[2], 16)];
};

const rgbTint = (r, g, b) => (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);

export class SuperEmitter {
    constructor(parent, art, config) {
        this._parent = parent;

        // art: array of Textures, or animation descriptors {framerate, loop, textures:[names]}
        this.animated = !!(art[0] && art[0].textures);
        if (this.animated) {
            this.anims = art.map(a => ({
                framerate: a.framerate,
                loop: a.loop !== false,
                textures: a.textures.map(t => typeof t === 'string' ? PIXI.Texture.from(t) : t),
            }));
        } else {
            this.textures = art.map(t => typeof t === 'string' ? PIXI.Texture.from(t) : t);
        }

        this.alphaStart = config.alpha ? config.alpha.start : 1;
        this.alphaEnd = config.alpha ? config.alpha.end : 1;
        this.scaleStart = config.scale ? config.scale.start : 1;
        this.scaleEnd = config.scale ? config.scale.end : 1;
        this.minimumScaleMultiplier = (config.scale && config.scale.minimumScaleMultiplier) || 1;
        this.colorStart = config.color ? hexToRgb(config.color.start) : null;
        this.colorEnd = config.color ? hexToRgb(config.color.end) : null;
        this.speedStart = config.speed ? config.speed.start : 0;
        this.speedEnd = config.speed ? config.speed.end : 0;
        this.minimumSpeedMultiplier = (config.speed && config.speed.minimumSpeedMultiplier) || 1;
        this.acceleration = {x: (config.acceleration && config.acceleration.x) || 0, y: (config.acceleration && config.acceleration.y) || 0};
        this.maxSpeed = config.maxSpeed || Infinity;
        this.minStartRotation = config.startRotation ? config.startRotation.min : 0;
        this.maxStartRotation = config.startRotation ? config.startRotation.max : 0;
        this.noRotation = !!config.noRotation;
        this.minRotationSpeed = config.rotationSpeed ? config.rotationSpeed.min : 0;
        this.maxRotationSpeed = config.rotationSpeed ? config.rotationSpeed.max : 0;
        this.minLifetime = config.lifetime ? config.lifetime.min : 1;
        this.maxLifetime = config.lifetime ? config.lifetime.max : 1;
        this.landGrace = config.landGrace || 0;
        this.frequency = config.frequency || 0.01;
        this.emitterLifetime = config.emitterLifetime || -1;
        this.maxParticles = config.maxParticles || 500;
        this.addAtBack = !!config.addAtBack;
        this.useParticleContainer = !!config.particleContainer;
        this.interpolateSpawn = !!config.interpolateSpawn;
        this.maxSpawnInterpolationDistance = config.maxSpawnInterpolationDistance || Infinity;
        this.velocityStretch = config.velocityStretch || null;
        this.connectedTrail = config.connectedTrail || null;
        this.spawnType = config.spawnType || 'point';
        this.spawnCircle = config.spawnCircle || {x: 0, y: 0, r: 0};
        this.particlesPerWave = config.particlesPerWave || 1;
        this.angleStart = config.angleStart || 0;
        // pixi-particles exposes the emitter rotation in degrees and adds it
        // to every particle's local start rotation. Several prefabs update
        // this directly while their bodies rotate, so keep the same contract.
        this.rotation = 0;
        this.ease = config.ease || null;
        this.spawnPos = new PIXI.Point(config.pos ? config.pos.x : 0, config.pos ? config.pos.y : 0);
        this.spawnVelocityX = 0;
        this.spawnVelocityY = 0;

        this.emit = config.emit !== false;
        this._emitterLife = this.emitterLifetime;
        this._spawnTimer = 0;
        this._completeCallback = undefined;
        this.randomColors = null;
        this.colorCycle = null;
        this._colorTime = 0;
        this._hasPreviousSpawn = false;
        this._previousSpawnX = this.spawnPos.x;
        this._previousSpawnY = this.spawnPos.y;
        this._previousRotation = this.rotation;
        this._trailSpawnIndex = 0;

        this._active = [];
        this._free = [];
        this._freeParticles = [];
    }

    get parent() { return this._parent; }
    set parent(c) {
        this.cleanup();
        this._parent = c;
    }

    // API compat: pixi-particles allowed swapping the particle class; our
    // animation mode is detected from the art format instead.
    set particleConstructor(_ctor) {}
    get particleConstructor() { return this.animated ? AnimatedParticle : Object; }

    setRandomColors(colors) {
        this.randomColors = colors.map(hexToRgb);
    }

    setColors(start, end = start) {
        this.colorStart = hexToRgb(start);
        this.colorEnd = hexToRgb(end);
        this.randomColors = null;
        this.colorCycle = null;
    }

    setColorCycle(colors, interval, time = 0) {
        this.colorCycle = colors.map(hexToRgb);
        this.colorInterval = Math.max(0.001, interval);
        this._colorTime = time;
        this.randomColors = null;
    }

    setSpawnVelocity(x = 0, y = 0) {
        this.spawnVelocityX = Number.isFinite(x) ? x : 0;
        this.spawnVelocityY = Number.isFinite(y) ? y : 0;
    }

    playOnce(callback) {
        this._completeCallback = callback;
        this._emitterLife = this.emitterLifetime;
        this._spawnTimer = 0;
        this.setSpawnVelocity();
        this._previousSpawnX = this.spawnPos.x;
        this._previousSpawnY = this.spawnPos.y;
        this._previousRotation = this.rotation;
        this._trailSpawnIndex = 0;
        this._hasPreviousSpawn = true;
        this.emit = true;
    }

    _ease(t) {
        if (!this.ease) return t;
        if (typeof this.ease === 'function') return this.ease(t);

        const n = this.ease.length;
        if (!n) return t;

        let i = Math.min(Math.floor(t * n), n - 1);
        i = Math.max(i, 0);
        const seg = this.ease[i];
        if (!seg) return t;
        const localT = t * n - i;
        // quadratic bezier through s, cp, e
        const omt = 1 - localT;
        return omt * omt * seg.s + 2 * omt * localT * seg.cp + localT * localT * seg.e;
    }

    _setScale(display, uniformScale, speed) {
        const particle = display.__particle;
        particle.renderScale = uniformScale;
        particle.renderSpeed = speed;

        let scaleX = uniformScale;
        let scaleY = uniformScale;

        if (this.velocityStretch) {
            const referenceSpeed = Math.max(1, this.velocityStretch.referenceSpeed || 500);
            const maxStretch = Math.max(1, this.velocityStretch.max || 1);
            const stretch = 1 + (maxStretch - 1) * Math.min(1, Math.max(0, speed / referenceSpeed));
            scaleX *= stretch;
            scaleY *= this.velocityStretch.width || 1;
        }

        if (display.__particle.lightweight) {
            display.scaleX = scaleX;
            display.scaleY = scaleY;
        } else {
            display.scale.set(scaleX, scaleY);
        }
    }

    _renderConnectedTrail() {
        const trail = this.connectedTrail;
        if (!trail || this._active.length === 0) return;

        const maxLinkDistance = trail.maxLinkDistance || Infinity;
        const maxLinkDistanceSq = maxLinkDistance * maxLinkDistance;
        const overlap = trail.overlap === undefined ? 1 : trail.overlap;
        const width = trail.width || 1;

        // Core particles double as rounded segments between consecutive
        // ballistic samples. Loose particles remain ordinary droplets, so the
        // stream keeps natural splatter without turning that randomness into a
        // jagged, lightning-shaped polyline. This stays in the same batch: no
        // filter pass, framebuffer, Graphics rebuild, or extra display object.
        let newerCore = null;
        for (let i = this._active.length - 1; i >= 0; i--) {
            const display = this._active[i];
            const particle = display.__particle;
            if (!particle.trailCore) continue;

            const targetX = newerCore ? newerCore.x : (this.emit ? this.spawnPos.x : particle.x);
            const targetY = newerCore ? newerCore.y : (this.emit ? this.spawnPos.y : particle.y);
            const dx = targetX - particle.x;
            const dy = targetY - particle.y;
            const distanceSq = dx * dx + dy * dy;

            if (distanceSq > 0.01 && distanceSq <= maxLinkDistanceSq) {
                const distance = Math.sqrt(distanceSq);
                const textureWidth = Math.max(1, display.texture.width);
                const baseScale = particle.renderScale;
                const capLength = textureWidth * baseScale;
                const scaleX = (distance + capLength * overlap) / textureWidth;
                const scaleY = baseScale * width;

                display.x = particle.x + dx * 0.5;
                display.y = particle.y + dy * 0.5;
                display.rotation = Math.atan2(dy, dx);
                if (particle.lightweight) {
                    display.scaleX = scaleX;
                    display.scaleY = scaleY;
                } else {
                    display.scale.set(scaleX, scaleY);
                }
            } else {
                display.x = particle.x;
                display.y = particle.y;
            }
            newerCore = particle;
        }
    }

    _spawnParticle(
        spawnX = this.spawnPos.x,
        spawnY = this.spawnPos.y,
        emitterRotation = this.rotation,
        firstUpdateDelta
    ) {
        if (this._active.length >= this.maxParticles) return;

        const lightweight = this._parent instanceof PIXI.ParticleContainer;
        let sprite = lightweight ? this._freeParticles.pop() : this._free.pop();
        if (!sprite) {
            if (lightweight) {
                const initialTexture = this.animated ? this.anims[0].textures[0] : this.textures[0];
                sprite = new PIXI.Particle(initialTexture);
                sprite.anchorX = 0.5;
                sprite.anchorY = 0.5;
            } else {
                sprite = new PIXI.Sprite();
                sprite.anchor.set(0.5);
            }
        }

        const p = sprite.__particle || (sprite.__particle = {});
        p.lightweight = lightweight;
        const trail = this.connectedTrail;
        const scatterEvery = trail ? Math.max(0, trail.scatterEvery | 0) : 0;
        p.trailCore = !!trail && (
            scatterEvery < 2 || this._trailSpawnIndex++ % scatterEvery !== scatterEvery - 1
        );
        p.age = 0;
        p.firstUpdateDelta = firstUpdateDelta;
        p.maxLife = this.minLifetime + Math.random() * (this.maxLifetime - this.minLifetime);
        p.scaleMult = this.minimumScaleMultiplier + Math.random() * (1 - this.minimumScaleMultiplier);
        p.speedMult = this.minimumSpeedMultiplier + Math.random() * (1 - this.minimumSpeedMultiplier);
        if (p.trailCore) {
            if (trail.lifetime !== undefined) p.maxLife = trail.lifetime;
            if (trail.scaleMultiplier !== undefined) p.scaleMult = trail.scaleMultiplier;
            if (trail.speedMultiplier !== undefined) p.speedMult = trail.speedMultiplier;
        }
        p.rotationSpeed = (this.minRotationSpeed + Math.random() * (this.maxRotationSpeed - this.minRotationSpeed)) * DEG2RAD;

        let angleDegrees;
        let ringAngle = null;
        if (this.spawnType === 'burst') {
            angleDegrees = this.angleStart + (360 / this.particlesPerWave) * this._waveIndex;
        } else if (this.spawnType === 'ring') {
            // Somewhere on the circle, heading away from its centre.
            const around = Math.random() * 360;
            ringAngle = (around + emitterRotation) * DEG2RAD;
            angleDegrees = around + this.minStartRotation + Math.random() * (this.maxStartRotation - this.minStartRotation);
        } else if (p.trailCore) {
            angleDegrees = trail.angle === undefined
                ? (this.minStartRotation + this.maxStartRotation) * 0.5
                : trail.angle;
        } else {
            angleDegrees = this.minStartRotation + Math.random() * (this.maxStartRotation - this.minStartRotation);
        }
        const angle = (angleDegrees + emitterRotation) * DEG2RAD;
        p.dirX = Math.cos(angle);
        p.dirY = Math.sin(angle);
        p.inheritedVelX = this.spawnVelocityX;
        p.inheritedVelY = this.spawnVelocityY;
        p.velX = p.dirX * this.speedStart * p.speedMult + p.inheritedVelX;
        p.velY = p.dirY * this.speedStart * p.speedMult + p.inheritedVelY;
        p.rotation = angle;

        sprite.x = spawnX;
        sprite.y = spawnY;
        if (ringAngle !== null) {
            sprite.x += Math.cos(ringAngle) * this.spawnCircle.r;
            sprite.y += Math.sin(ringAngle) * this.spawnCircle.r;
        }
        if (this.spawnType === 'circle') {
            const r = Math.random() * this.spawnCircle.r;
            const a = Math.random() * Math.PI * 2;
            const offsetX = this.spawnCircle.x + Math.cos(a) * r;
            const offsetY = this.spawnCircle.y + Math.sin(a) * r;
            const emitterRotationRad = emitterRotation * DEG2RAD;
            const cosRotation = Math.cos(emitterRotationRad);
            const sinRotation = Math.sin(emitterRotationRad);
            sprite.x += offsetX * cosRotation - offsetY * sinRotation;
            sprite.y += offsetX * sinRotation + offsetY * cosRotation;
        }
        p.x = sprite.x;
        p.y = sprite.y;

        if (this.colorCycle?.length) {
            const birthTime = Math.max(0, this._colorTime - (firstUpdateDelta || 0));
            p.color = this.colorCycle[Math.floor(birthTime / this.colorInterval + 1e-8) % this.colorCycle.length];
            p.colorFixed = true;
        } else if (this.randomColors && this.randomColors.length) {
            p.color = this.randomColors[(this.randomColors.length * Math.random()) | 0];
            p.colorFixed = true;
        } else {
            p.color = this.colorStart;
            p.colorFixed = false;
        }
        p.colorEnd = this.colorEnd;

        if (this.animated) {
            p.anim = this.anims[(this.anims.length * Math.random()) | 0];
            sprite.texture = p.anim.textures[0];
        } else {
            sprite.texture = this.textures[(this.textures.length * Math.random()) | 0];
        }

        if (!lightweight) sprite.visible = true;
        sprite.rotation = this.noRotation ? 0 : p.rotation;
        sprite.alpha = this.alphaStart;
        this._setScale(sprite, this.scaleStart * p.scaleMult, this.speedStart * p.speedMult);
        if (p.color) sprite.tint = rgbTint(p.color[0], p.color[1], p.color[2]);
        else sprite.tint = 0xFFFFFF;

        if (this._parent) {
            if (lightweight) {
                if (this.addAtBack) this._parent.addParticleAt(sprite, 0);
                else this._parent.addParticle(sprite);
            } else if (this.addAtBack) {
                this._parent.addChildAt(sprite, 0);
            } else {
                this._parent.addChild(sprite);
            }
            if (this._parent.hideWhenEmpty) this._parent.visible = true;
        }
        this._active.push(sprite);
    }

    update(delta) {
        this._colorTime += delta;
        const currentSpawnX = this.spawnPos.x;
        const currentSpawnY = this.spawnPos.y;
        const currentRotation = this.rotation;
        if (!this._hasPreviousSpawn) {
            this._previousSpawnX = currentSpawnX;
            this._previousSpawnY = currentSpawnY;
            this._previousRotation = currentRotation;
            this._hasPreviousSpawn = true;
        }

        let previousSpawnX = this._previousSpawnX;
        let previousSpawnY = this._previousSpawnY;
        let previousRotation = this._previousRotation;
        const spawnDeltaX = currentSpawnX - previousSpawnX;
        const spawnDeltaY = currentSpawnY - previousSpawnY;
        if (
            spawnDeltaX * spawnDeltaX + spawnDeltaY * spawnDeltaY >
            this.maxSpawnInterpolationDistance * this.maxSpawnInterpolationDistance
        ) {
            previousSpawnX = currentSpawnX;
            previousSpawnY = currentSpawnY;
            previousRotation = currentRotation;
        }
        const rotationDelta = (
            ((currentRotation - previousRotation + 180) % 360 + 360) % 360
        ) - 180;

        // spawning
        if (this.emit) {
            this._spawnTimer -= delta;
            if (this._spawnTimer <= 0) {
                const frequency = Math.max(0.0001, this.frequency);
                const dueWaves = Math.floor(-this._spawnTimer / frequency) + 1;
                const availableParticles = Math.max(0, this.maxParticles - this._active.length);
                const wavesToSpawn = Math.min(
                    dueWaves,
                    Math.ceil(availableParticles / this.particlesPerWave)
                );
                const firstEventTime = delta + this._spawnTimer;

                for (let wave = 0; wave < wavesToSpawn; wave++) {
                    const eventTime = firstEventTime + wave * frequency;
                    const progress = delta > 0 ? Math.min(1, Math.max(0, eventTime / delta)) : 1;
                    const spawnX = this.interpolateSpawn
                        ? previousSpawnX + (currentSpawnX - previousSpawnX) * progress
                        : currentSpawnX;
                    const spawnY = this.interpolateSpawn
                        ? previousSpawnY + (currentSpawnY - previousSpawnY) * progress
                        : currentSpawnY;
                    const spawnRotation = this.interpolateSpawn
                        ? previousRotation + rotationDelta * progress
                        : currentRotation;

                    this._waveIndex = 0;
                    for (let w = 0; w < this.particlesPerWave; w++, this._waveIndex++) {
                        // Only simulate the portion of this frame after the
                        // sub-frame emission time. Giving every new particle a
                        // full-frame step creates a visible gap at the nozzle
                        // and bunches samples into frame-sized zigzags.
                        this._spawnParticle(
                            spawnX,
                            spawnY,
                            spawnRotation,
                            delta * (1 - progress)
                        );
                    }
                }

                // Advance past every due wave, including waves discarded because
                // the emitter is full. This prevents a slow frame from creating a
                // catch-up loop that makes the next frame even slower.
                this._spawnTimer += dueWaves * frequency;
            }
            if (this.emitterLifetime > 0) {
                this._emitterLife -= delta;
                if (this._emitterLife <= 0) this.emit = false;
            }
        }
        this._previousSpawnX = currentSpawnX;
        this._previousSpawnY = currentSpawnY;
        this._previousRotation = currentRotation;

        // particle simulation
        const hasAccel = this.acceleration.x !== 0 || this.acceleration.y !== 0;
        for (let i = this._active.length - 1; i >= 0; i--) {
            const sprite = this._active[i];
            const p = sprite.__particle;
            const particleDelta = p.firstUpdateDelta === undefined
                ? delta
                : p.firstUpdateDelta;
            p.firstUpdateDelta = undefined;
            p.age += particleDelta;
            // A drop that can land keeps falling for a while after its life, until it does.
            if (p.age >= p.maxLife + (this.collider ? this.landGrace : 0)) {
                this._recycle(i);
                continue;
            }
            const t = this._ease(Math.min(1, p.age / p.maxLife));
            const fromX = p.x;
            const fromY = p.y;

            sprite.alpha = this.alphaStart + (this.alphaEnd - this.alphaStart) * t;

            if (p.color && !p.colorFixed && p.colorEnd) {
                sprite.tint = rgbTint(
                    p.color[0] + (p.colorEnd[0] - p.color[0]) * t,
                    p.color[1] + (p.colorEnd[1] - p.color[1]) * t,
                    p.color[2] + (p.colorEnd[2] - p.color[2]) * t,
                );
            }

            let speed;
            if (hasAccel) {
                p.velX += this.acceleration.x * particleDelta;
                p.velY += this.acceleration.y * particleDelta;
                speed = Math.sqrt(p.velX * p.velX + p.velY * p.velY);
                if (speed > this.maxSpeed) {
                    const f = this.maxSpeed / speed;
                    p.velX *= f;
                    p.velY *= f;
                    speed = this.maxSpeed;
                }
                p.x += p.velX * particleDelta;
                p.y += p.velY * particleDelta;
                if (!this.noRotation) sprite.rotation = Math.atan2(p.velY, p.velX);
            } else {
                const directedSpeed = (this.speedStart + (this.speedEnd - this.speedStart) * t) * p.speedMult;
                const velocityX = p.dirX * directedSpeed + p.inheritedVelX;
                const velocityY = p.dirY * directedSpeed + p.inheritedVelY;
                speed = Math.sqrt(velocityX * velocityX + velocityY * velocityY);
                p.x += velocityX * particleDelta;
                p.y += velocityY * particleDelta;
                if (!this.noRotation) {
                    sprite.rotation = this.velocityStretch
                        ? Math.atan2(velocityY, velocityX)
                        : p.rotation + p.rotationSpeed * p.age;
                }
            }
            if (this.collider && this.collider(this, p, sprite, fromX, fromY)) {
                this._recycle(i);
                continue;
            }
            sprite.x = p.x;
            sprite.y = p.y;

            this._setScale(
                sprite,
                (this.scaleStart + (this.scaleEnd - this.scaleStart) * t) * p.scaleMult,
                speed
            );

            if (p.anim) {
                const frame = Math.floor(p.age * p.anim.framerate);
                const len = p.anim.textures.length;
                sprite.texture = p.anim.textures[p.anim.loop ? frame % len : Math.min(frame, len - 1)];
            }
        }

        this._renderConnectedTrail();

        // playOnce completion: emission over and no live particles left
        if (!this.emit && this._completeCallback && this._active.length === 0) {
            const cb = this._completeCallback;
            this._completeCallback = undefined;
            cb();
        }
    }

    _recycle(index) {
        const sprite = this._active[index];
        this._active.splice(index, 1);
        if (sprite.__particle.lightweight) {
            this._parent.removeParticle(sprite);
            this._freeParticles.push(sprite);
        } else {
            sprite.visible = false;
            const parent = sprite.parent;
            if (parent) {
                parent.removeChild(sprite);
                if (parent.hideWhenEmpty && !parent.children.length) parent.visible = false;
            }
            this._free.push(sprite);
        }
    }

    cleanup() {
        for (let i = this._active.length - 1; i >= 0; i--) this._recycle(i);
        this.emit = false;
        this._hasPreviousSpawn = false;
        this._trailSpawnIndex = 0;
    }

    destroy() {
        this.cleanup();
        this._free.forEach(sprite => sprite.destroy());
        this._free.length = 0;
        this._freeParticles.length = 0;
        this._parent = null;
    }
}
